// What a file is, decided from its bytes (TASK-FILES). Work OS echoed the client's `file.type`
// back as the served `Content-Type` (`app/api/files/[id]/route.ts`), so an uploaded HTML or SVG
// file rendered on the app's origin; here the stored type is sniffed from the magic bytes (the
// client's type and the extension only refine a text or a container format), and how a file may
// be shown inline is decided once, from the stored type, by `previewOf` / `inlineSafe`.

/** Magic numbers of the types a task file commonly is (first bytes; offset where not 0). */
const MAGIC: { mime: string; bytes: number[]; offset?: number }[] = [
  { mime: "image/png", bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
  { mime: "image/jpeg", bytes: [0xff, 0xd8, 0xff] },
  { mime: "image/gif", bytes: [0x47, 0x49, 0x46, 0x38] },
  { mime: "image/bmp", bytes: [0x42, 0x4d] },
  { mime: "image/x-icon", bytes: [0x00, 0x00, 0x01, 0x00] },
  { mime: "application/pdf", bytes: [0x25, 0x50, 0x44, 0x46, 0x2d] },
  { mime: "application/zip", bytes: [0x50, 0x4b, 0x03, 0x04] },
  { mime: "application/gzip", bytes: [0x1f, 0x8b] },
  { mime: "application/x-7z-compressed", bytes: [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c] },
  { mime: "application/vnd.rar", bytes: [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07] },
  { mime: "application/x-ole-storage", bytes: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1] },
  { mime: "audio/mpeg", bytes: [0x49, 0x44, 0x33] },
  { mime: "audio/flac", bytes: [0x66, 0x4c, 0x61, 0x43] },
  { mime: "audio/ogg", bytes: [0x4f, 0x67, 0x67, 0x53] },
  { mime: "video/webm", bytes: [0x1a, 0x45, 0xdf, 0xa3] },
  { mime: "application/wasm", bytes: [0x00, 0x61, 0x73, 0x6d] },
  { mime: "application/x-executable", bytes: [0x7f, 0x45, 0x4c, 0x46] },
  { mime: "application/x-msdownload", bytes: [0x4d, 0x5a] },
];

/** Extensions that name a type inside a zip or OLE container, or a text format. */
const BY_EXT: Record<string, string> = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  doc: "application/msword",
  xls: "application/vnd.ms-excel",
  ppt: "application/vnd.ms-powerpoint",
  odt: "application/vnd.oasis.opendocument.text",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  txt: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  csv: "text/csv",
  tsv: "text/tab-separated-values",
  json: "application/json",
  xml: "application/xml",
  yaml: "application/yaml",
  yml: "application/yaml",
  html: "text/html",
  htm: "text/html",
  svg: "image/svg+xml",
  css: "text/css",
  js: "text/javascript",
  mjs: "text/javascript",
  ts: "text/plain",
  tsx: "text/plain",
  jsx: "text/plain",
  py: "text/x-python",
  rb: "text/plain",
  go: "text/plain",
  rs: "text/plain",
  java: "text/plain",
  c: "text/plain",
  h: "text/plain",
  cpp: "text/plain",
  sh: "text/x-shellscript",
  sql: "text/plain",
  log: "text/plain",
  ini: "text/plain",
  toml: "text/plain",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  ogg: "audio/ogg",
  webp: "image/webp",
  avif: "image/avif",
  heic: "image/heic",
};

const extOf = (name: string): string => {
  const i = name.lastIndexOf(".");
  return i < 0 ? "" : name.slice(i + 1).toLowerCase();
};

const starts = (b: Uint8Array, sig: number[], offset = 0) =>
  b.length >= offset + sig.length && sig.every((x, i) => b[offset + i] === x);

const ascii = (b: Uint8Array, from: number, to: number) =>
  String.fromCharCode(...b.subarray(from, Math.min(to, b.length)));

/** Valid UTF-8 with no NUL in the first 8 KiB: text. */
export function looksLikeText(b: Uint8Array): boolean {
  const head = b.subarray(0, 8192);
  if (head.includes(0)) return false;
  try {
    new TextDecoder("utf-8", { fatal: true }).decode(
      // A multi-byte sequence cut at the window's end is not an error.
      head.length === b.length ? head : head.subarray(0, head.length - 4),
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * The stored type of an upload: the magic bytes first; a zip/OLE container or text refined by the
 * extension; markup (HTML, SVG, XML) recognised in text so it is never served as something
 * renderable by accident; otherwise `application/octet-stream`. `claimed` (the client's type) is
 * used only to refine text (`text/csv` for a `.dat`), never to make bytes renderable.
 */
export function sniffMime(bytes: Uint8Array, fileName: string, claimed?: string | null): string {
  const ext = extOf(fileName);
  // ISO BMFF (mp4, mov, m4a, heic, avif): `ftyp` at offset 4.
  if (bytes.length >= 12 && ascii(bytes, 4, 8) === "ftyp") {
    const brand = ascii(bytes, 8, 12);
    if (brand.startsWith("heic") || brand.startsWith("heix") || brand.startsWith("mif1"))
      return "image/heic";
    if (brand.startsWith("avif")) return "image/avif";
    if (brand.startsWith("qt")) return "video/quicktime";
    if (brand.startsWith("M4A")) return "audio/mp4";
    return "video/mp4";
  }
  // RIFF containers: WEBP / WAVE / AVI.
  if (starts(bytes, [0x52, 0x49, 0x46, 0x46]) && bytes.length >= 12) {
    const kind = ascii(bytes, 8, 12);
    if (kind === "WEBP") return "image/webp";
    if (kind === "WAVE") return "audio/wav";
    if (kind === "AVI ") return "video/x-msvideo";
  }
  for (const m of MAGIC) {
    if (!starts(bytes, m.bytes, m.offset)) continue;
    if (m.mime === "application/zip" || m.mime === "application/x-ole-storage") {
      const byExt = BY_EXT[ext];
      if (byExt?.startsWith("application/")) return byExt;
      return m.mime === "application/zip" ? m.mime : "application/octet-stream";
    }
    if (m.mime === "audio/ogg" && ext === "ogv") return "video/ogg";
    return m.mime;
  }
  if (looksLikeText(bytes)) {
    const head = new TextDecoder().decode(bytes.subarray(0, 1024)).trimStart().toLowerCase();
    if (head.startsWith("<svg") || (head.startsWith("<?xml") && head.includes("<svg")))
      return "image/svg+xml";
    if (
      head.startsWith("<!doctype html") ||
      head.startsWith("<html") ||
      ext === "html" ||
      ext === "htm"
    )
      return "text/html";
    const byExt = BY_EXT[ext];
    if (
      byExt &&
      (byExt.startsWith("text/") || byExt.startsWith("application/") || byExt === "image/svg+xml")
    )
      return byExt === "image/svg+xml" ? "text/plain" : byExt;
    const c = claimed?.split(";")[0]?.trim().toLowerCase();
    if (c && /^text\/(plain|csv|markdown|tab-separated-values)$/.test(c)) return c;
    return "text/plain";
  }
  return "application/octet-stream";
}

export type Preview = "image" | "pdf" | "text" | "video" | "audio" | "none";

/** Raster images a browser draws (never SVG: it is a document that can run script). */
const RASTER = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/bmp",
  "image/x-icon",
]);
const TEXTUAL =
  /^(text\/(plain|markdown|csv|tab-separated-values|css|javascript|x-python|x-shellscript)|application\/(json|yaml|xml))$/;

/** How a client may preview the file (and whether `files.content` serves it inline). */
export function previewOf(mime: string): Preview {
  if (RASTER.has(mime)) return "image";
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (TEXTUAL.test(mime)) return "text";
  return "none";
}

/** Whether a thumbnail can be made (raster images; a PDF's first page). */
export const thumbable = (mime: string): boolean =>
  (RASTER.has(mime) && mime !== "image/x-icon") || mime === "application/pdf";

/**
 * The `Content-Type` the proxy sends: previews as stored (text as `text/plain` so no browser
 * runs it), everything else — HTML, SVG, XML, executables, office files — as an opaque download.
 */
export function servedType(mime: string): string {
  const p = previewOf(mime);
  if (p === "text") return "text/plain; charset=utf-8";
  if (p === "none") return "application/octet-stream";
  return mime;
}

/** Extracted text is worth having: text files (any textual type, HTML and SVG included). */
export const textual = (mime: string): boolean =>
  mime.startsWith("text/") ||
  mime === "image/svg+xml" ||
  mime === "application/json" ||
  mime === "application/yaml" ||
  mime === "application/xml";

/**
 * A file name safe to store and to put in a header: no path separators or control characters,
 * trimmed, at most 255 characters, never empty (`file`).
 */
export function cleanFileName(name: string): string {
  const s = Array.from(name.normalize("NFC"))
    .filter((ch) => {
      const c = ch.codePointAt(0) ?? 0;
      return c >= 0x20 && c !== 0x7f && ch !== "/" && ch !== "\\";
    })
    .join("")
    .trim()
    .replace(/^\.+/, "")
    .slice(0, 255)
    .trim();
  return s || "file";
}

/** `Content-Disposition` with an ASCII fallback and the RFC 5987 UTF-8 name. */
export function contentDisposition(kind: "inline" | "attachment", name: string): string {
  const fallback = name.replace(/[^\x20-\x7e]|["\\%;]/g, "_");
  return `${kind}; filename="${fallback}"; filename*=UTF-8''${encodeURIComponent(name)}`;
}
